/* eslint-disable jsx-a11y/label-has-associated-control */
import { useState, useEffect, ChangeEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ReactComponent as Cloud } from '@/assets/image/Cloud.svg';
import PreviewFiles from './PreviewFiles';
import { compressImage, shouldCompress } from '@/utils/imageCompression';

export interface FileData {
  _id: string | number; // Replace with appropriate type for `idx`
  title: string;
  url: string;
  local: boolean;
  file: File;
  document?: string;
}

interface UploadProps {
  uniquename: string;
  // eslint-disable-next-line no-unused-vars
  onChange?: (files: FileData[]) => void;
  className?: string;
  multiple?: boolean;
  value?: FileData[];
  // eslint-disable-next-line no-unused-vars
  customFn?: (file: FileData) => void;
}

const Upload: React.FC<UploadProps> = ({ onChange, value, customFn }) => {
  const [files, setFiles] = useState<FileData[]>([]);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles: FileData[] = [];
    const maxFileSize = 2 * 1024 * 1024; // 2MB limit

    if (e.target.files) {
      // Convert to array and process files concurrently to avoid no-await-in-loop error
      const fileArray = Array.from(e.target.files);
      const processFilePromises = fileArray.map(async (file) => {
        try {
          let processedFile = file;
          
          // Check if file needs compression
          if (shouldCompress(file) && file.type.startsWith('image/')) {
            try {
              processedFile = await compressImage(file, { maxSizeMB: 1, maxWidth: 512, maxHeight: 512, quality: 0.8 });
            } catch (compressionError) {
              // Using console.error as a better solution for file processing errors

              return null;
            }
          } else if (file.size > maxFileSize) {
            // Using console.error as a better solution for file size errors

            return null;
          }

          return { 
            _id: uuidv4(), 
            title: processedFile.name, 
            file: processedFile, 
            url: URL.createObjectURL(processedFile), 
            local: true 
          };
        } catch (error) {
          // Using console.error as a better solution for general file processing errors

          return null;
        }
      });

      // Wait for all file processing to complete
      const processedFiles = await Promise.all(processFilePromises);
      
      // Filter out null values (files that failed processing)
      const validFiles = processedFiles.filter(file => file !== null) as FileData[];
      uploadedFiles.push(...validFiles);
    }

    setFiles(uploadedFiles);
    if (onChange) onChange(uploadedFiles);
  };

  const handleFileDelete = (fileToDelete: FileData) => {
    if (files) {
      const updatedFiles = files.filter((file) => file !== fileToDelete);
      setFiles(updatedFiles);
    }
  };

  useEffect(() => {
    setFiles(value || []);
  }, [value]);

  return (
    <>
      <div className="upload flex flex-col gap-3">
        <input type="file" id="thumbnail" name="thumbnail" className="hidden" onChange={handleFileChange} />

        <label
          id="thumbnail"
          htmlFor="thumbnail"
          className="flex min-h-[5rem] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-400 bg-[#f5f5f5] box-decoration-slice px-5 py-3"
        >
          <Cloud />
          <p className="tooltip font-inter text-xs font-semibold text-[#475467]">Upload your own Icon</p>
        </label>
        {files.length > 0 && (
          <PreviewFiles files={files} handleFileDelete={handleFileDelete} customDeleteFn={customFn} />
        )}
      </div>
    </>
  );
};

export default Upload;